import { useEffect, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  alpha,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Stack,
  Typography,
} from '@mui/material';
import {
  CalendarToday,
  Description,
  Download,
  ExpandMore,
  FileDownload,
  GroupAdd,
  HelpOutline,
  Inbox,
  ListAlt,
  Mail,
  Notes,
  UploadFile,
} from '@mui/icons-material';
import { IConfig } from '../../../Interfaces/IConfig';
import { DialogTopBar } from '../../core/DialogTopBar';

interface IProps {
  configObject: IConfig;
  isOpenParent?: boolean;
  setIsOpenParent?: Function;
  openOnPage?: string;
  disableButton?: boolean;
}

interface IHelpEntry {
  id: string;
  icon: React.ReactNode;
  question: string;
  answer: React.ReactNode;
}

export const HelpOverview: React.FC<IProps> = ({
  configObject,
  isOpenParent,
  setIsOpenParent,
  openOnPage,
  disableButton,
}) => {
  const [expanded, setExpanded] = useState<string | false>(
    openOnPage === undefined ? false : openOnPage
  );
  const [isOpenDialog, setIsOpenDialog] = useState(false);

  useEffect(() => {
    if (isOpenParent !== undefined) {
      setIsOpenDialog(isOpenParent);
    }
  }, [isOpenParent]);

  const handleChange =
    (panel: string) => (_: React.SyntheticEvent, isExpanded: boolean) => {
      setExpanded(isExpanded ? panel : false);
    };

  const handleClose = () => {
    setIsOpenDialog(false);
    if (setIsOpenParent) {
      setIsOpenParent(false);
    }
  };

  const helpEntries: IHelpEntry[] = [
    {
      id: 'get_documents',
      icon: <Description fontSize='small' />,
      question: 'Wo finde ich meine Dokumente?',
      answer: (
        <>
          <Typography variant='body2' sx={{ mb: 1.5 }}>
            Ihre Dokumente wie Urkunden und Rechnungen finden Sie im jeweiligen
            Vorgang. Öffnen Sie den Vorgang und wechseln Sie im Bereich
            <strong> Dokumente </strong>auf den passenden Filter
            (z.&nbsp;B. <em>Urkunden</em>, <em>Rechnungen</em>, <em>Entwürfe</em>).
          </Typography>
          <Typography variant='body2'>
            Eine vorgangsübergreifende Übersicht finden Sie zusätzlich unter
            <strong> Meine Dokumente </strong>auf dem Dashboard.
          </Typography>
        </>
      ),
    },
    {
      id: 'download_documents',
      icon: <Download fontSize='small' />,
      question: 'Wie erhalte ich angeforderte Dokumente?',
      answer: (
        <>
          <Typography variant='body2' sx={{ mb: 1.5 }}>
            Bei jedem Dokument sehen Sie eine Schaltfläche:
          </Typography>
          <Stack spacing={1} sx={{ mb: 1.5 }}>
            <Stack direction='row' spacing={1} alignItems='flex-start'>
              <Download fontSize='small' sx={{ mt: 0.25, color: 'primary.main' }} />
              <Typography variant='body2'>
                <strong>Herunterladen</strong> — Das Dokument ist bereits verfügbar
                und kann direkt heruntergeladen werden.
              </Typography>
            </Stack>
            <Stack direction='row' spacing={1} alignItems='flex-start'>
              <FileDownload fontSize='small' sx={{ mt: 0.25, color: 'primary.main' }} />
              <Typography variant='body2'>
                <strong>Anfordern</strong> — Das Dokument muss erst beim Notarbüro
                angefordert werden. Es steht Ihnen anschließend unter{' '}
                <strong>Meine Dokumente</strong> zum Download bereit
                (Bereitstellung kann bis zu 5&nbsp;Minuten dauern).
              </Typography>
            </Stack>
          </Stack>
          <Typography variant='body2' sx={{ color: 'text.secondary' }}>
            Aus Sicherheitsgründen werden Dokumente in <strong>Meine Dokumente</strong>{' '}
            täglich um Mitternacht automatisch gelöscht und müssen bei Bedarf
            erneut angefordert werden.
          </Typography>
        </>
      ),
    },
    {
      id: 'get_draft',
      icon: <Notes fontSize='small' />,
      question: 'Wo finde ich den Vertragsentwurf?',
      answer: (
        <Typography variant='body2'>
          Öffnen Sie den relevanten Vorgang und wählen Sie im Bereich
          <strong> Dokumente </strong>den Filter <strong>Entwürfe</strong>.
          Dort sehen Sie alle bereitgestellten Vertragsentwürfe sowie eingegangene
          Kommentare.
        </Typography>
      ),
    },
    {
      id: 'write_draft',
      icon: <Mail fontSize='small' />,
      question: 'Wie kann ich Anmerkungen zum Entwurf einbringen?',
      answer: (
        <>
          <Typography variant='body2' sx={{ mb: 1.5 }}>
            Im Bereich <strong>Entwürfe</strong> finden Sie eine Klickfläche
            <strong> Kommentar hinzufügen</strong>. Hier können Sie
            Änderungswünsche, Anmerkungen oder Bedenken hinterlegen — sichtbar
            für alle am Vorgang Beteiligten.
          </Typography>
          <Typography variant='body2' sx={{ color: 'text.secondary' }}>
            <strong>Tipp:</strong> An Kommentaren können auch Dateien
            (z.&nbsp;B. Word-Dokumente) angehängt werden, die alle Beteiligten
            herunterladen können.
          </Typography>
        </>
      ),
    },
    {
      id: 'requirement',
      icon: <ListAlt fontSize='small' />,
      question: 'Was ist der Statusverlauf?',
      answer: (
        <>
          <Typography variant='body2' sx={{ mb: 1.5 }}>
            Jeder Vorgang besteht aus mehreren Phasen, die wiederum mehrere
            Maßnahmen enthalten. Damit der Vorgang abgeschlossen werden kann,
            müssen alle Maßnahmen erledigt sein. Manche Maßnahmen erfordern
            Ihre Mitwirkung — etwa eine Bestätigung oder ein hochzuladendes
            Dokument.
          </Typography>
          <Stack spacing={1}>
            <Stack direction='row' spacing={1} alignItems='flex-start'>
              <UploadFile fontSize='small' sx={{ mt: 0.25, color: 'primary.main' }} />
              <Typography variant='body2'>
                Hochladen-Symbol: Klicken Sie es an, um ein Dokument zu dieser
                Maßnahme hochzuladen (z.&nbsp;B. Kontoauszug, Bestätigung).
              </Typography>
            </Stack>
            <Stack direction='row' spacing={1} alignItems='flex-start'>
              <FileDownload fontSize='small' sx={{ mt: 0.25, color: 'primary.main' }} />
              <Typography variant='body2'>
                Herunterladen-Symbol: Hier stellt das Notarbüro ein Dokument
                speziell zu dieser Maßnahme bereit.
              </Typography>
            </Stack>
          </Stack>
          <Typography variant='caption' sx={{ display: 'block', mt: 1.5, color: 'text.disabled' }}>
            Hinweis: Nicht jede Vorgangsart enthält einen Statusverlauf.
          </Typography>
        </>
      ),
    },
    {
      id: 'invitate',
      icon: <GroupAdd fontSize='small' />,
      question: 'Wie lade ich weitere Personen zum Vorgang ein?',
      answer: (
        <>
          <Typography variant='body2' sx={{ mb: 1.5 }}>
            Im geöffneten Vorgang finden Sie — sofern Sie über die nötige
            Berechtigung verfügen — die Schaltfläche
            <strong> Person einladen</strong>. Geben Sie die
            E-Mail-Adresse der Person ein und senden Sie die Einladung ab.
          </Typography>
          <Typography variant='body2' sx={{ color: 'text.secondary' }}>
            Die Einladung ist nach dem Versand 24&nbsp;Stunden gültig und kann
            nicht mehr zurückgenommen werden — bitte achten Sie auf die korrekte
            E-Mail-Adresse.
          </Typography>
        </>
      ),
    },
    {
      id: 'inbox_reply',
      icon: <Inbox fontSize='small' />,
      question: 'Wie kann ich auf eine Nachricht des Notarbüros antworten?',
      answer: (
        <>
          <Typography variant='body2' sx={{ mb: 1.5 }}>
            Im <strong>Posteingang</strong> (erreichbar über das Dashboard) sehen
            Sie alle Nachrichten Ihres Notarbüros. Öffnen Sie eine Nachricht und
            klicken Sie auf <strong>Antworten</strong>, um direkt zu antworten.
          </Typography>
          <Typography variant='body2' sx={{ mb: 1.5 }}>
            Ihre gesendeten Antworten sowie Rückmeldungen des Notarbüros erscheinen
            als Thread unterhalb der ursprünglichen Nachricht — so behalten Sie
            den gesamten Gesprächsverlauf im Blick.
          </Typography>
          <Typography variant='body2' sx={{ color: 'text.secondary' }}>
            Neue Nachrichten und Antworten des Notarbüros werden als ungelesen
            markiert und sind am blauen Punkt in der Liste erkennbar.
          </Typography>
        </>
      ),
    },
    {
      id: 'appointments',
      icon: <CalendarToday fontSize='small' />,
      question: 'Wo sehe ich meine Termine?',
      answer: (
        <>
          <Typography variant='body2' sx={{ mb: 1.5 }}>
            Anstehende Termine werden direkt im jeweiligen Vorgang angezeigt —
            unterhalb der Vorgangs-Kopfzeile im Bereich{' '}
            <strong>Anstehende Termine</strong>.
          </Typography>
          <Typography variant='body2' sx={{ color: 'text.secondary' }}>
            Dort finden Sie Datum, Uhrzeit, Ort sowie — falls vorhanden — einen
            direkten Link zum Online-Meeting.
          </Typography>
        </>
      ),
    },
    {
      id: 'helping',
      icon: <HelpOutline fontSize='small' />,
      question: 'Meine Frage wurde nicht beantwortet',
      answer: (
        <>
          <Typography variant='body2' sx={{ mb: 1.5 }}>
            Schreiben Sie uns gerne eine E-Mail an{' '}
            <Box
              component='a'
              href={`mailto:${configObject.email}`}
              sx={{ color: 'primary.main', fontWeight: 600 }}
            >
              {configObject.email}
            </Box>
            .
          </Typography>
          <Typography variant='body2' sx={{ color: 'text.secondary' }}>
            Bitte geben Sie uns die E-Mail-Adresse an, mit der Sie sich im
            Mandantenportal anmelden, sowie — falls vorhanden — die
            Vorgangsnummer Ihres Anliegens. Diese finden Sie im geöffneten
            Vorgang in den Grundinformationen.
          </Typography>
        </>
      ),
    },
  ];

  return (
    <>
      {!disableButton && (
        <Button onClick={() => setIsOpenDialog(true)} variant='outlined'>
          Hilfe
        </Button>
      )}

      <Dialog
        open={isOpenParent === undefined ? isOpenDialog : isOpenParent}
        onClose={handleClose}
        maxWidth='md'
        fullWidth
        PaperProps={{
          sx: theme => ({
            borderRadius: 3,
            boxShadow: `0 24px 64px ${alpha(theme.palette.primary.main, 0.18)}`,
          }),
        }}
      >
        <DialogTopBar
          icon={<HelpOutline />}
          title='Hilfe & Häufige Fragen'
          subtitle='Antworten zu typischen Fragen rund um Ihr Mandantenportal'
          onClose={handleClose}
        />

        <DialogContent sx={{ p: { xs: 2, sm: 3 }, backgroundColor: 'background.default' }}>
          <Stack spacing={1.25} sx={{ mt: 0.5 }}>
            {helpEntries.map(entry => {
              const isExpanded = expanded === entry.id;
              return (
                <Accordion
                  key={entry.id}
                  expanded={isExpanded}
                  onChange={handleChange(entry.id)}
                  disableGutters
                  elevation={0}
                  sx={theme => ({
                    border: `1px solid ${alpha(
                      theme.palette.primary.main,
                      isExpanded ? 0.28 : 0.1
                    )}`,
                    borderRadius: '12px !important',
                    overflow: 'hidden',
                    backgroundColor: isExpanded
                      ? alpha(theme.palette.primary.main, 0.02)
                      : 'background.paper',
                    transition: 'all 0.18s ease',
                    '&:before': { display: 'none' },
                    '&:hover': {
                      borderColor: alpha(theme.palette.primary.main, 0.24),
                    },
                  })}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMore />}
                    sx={{
                      px: 2,
                      py: 0.5,
                      minHeight: 56,
                      '& .MuiAccordionSummary-content': {
                        my: 1.25,
                        alignItems: 'center',
                        gap: 1.5,
                      },
                    }}
                  >
                    <Box
                      sx={theme => ({
                        width: 30,
                        height: 30,
                        borderRadius: '50%',
                        flexShrink: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: theme.palette.primary.main,
                        backgroundColor: alpha(theme.palette.primary.main, 0.1),
                      })}
                    >
                      {entry.icon}
                    </Box>
                    <Typography
                      variant='subtitle2'
                      sx={{ fontWeight: 600, lineHeight: 1.3 }}
                    >
                      {entry.question}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails
                    sx={theme => ({
                      px: 2,
                      pt: 0,
                      pb: 2,
                      pl: 5.75,
                      borderTop: `1px solid ${alpha(theme.palette.primary.main, 0.06)}`,
                    })}
                  >
                    <Box sx={{ pt: 1.5 }}>{entry.answer}</Box>
                  </AccordionDetails>
                </Accordion>
              );
            })}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 1.5 }}>
          <Button onClick={handleClose} variant='contained'>
            Schließen
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
